import { documentDisplayName } from '../../utils/documentNames';
import { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import SyncIcon from '@mui/icons-material/SyncOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlatOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import KeyIcon from '@mui/icons-material/KeyOutlined';
import { getStudentTimeline } from '../../api/students';
import { StudentTimelineEntry, TimelineAuditEvent } from '../../types/student';

/** "Notified the counselor and the student (portal; email not configured)"
 * — who a document rejection was sent to (Phase 99 audit `notified`). */
const notifiedSummary = (notified: unknown): string | null => {
  if (!notified || typeof notified !== 'object') return null;
  const n = notified as { counselorIds?: number[]; studentPortal?: boolean; studentEmail?: string | null };
  const parts: string[] = [];
  if (n.counselorIds && n.counselorIds.length > 0) {
    parts.push(n.counselorIds.length === 1 ? 'the counselor' : `${n.counselorIds.length} counselors`);
  }
  const channels = [
    n.studentPortal && 'portal',
    n.studentEmail === 'SENT' && 'email',
  ].filter(Boolean);
  if (channels.length > 0) parts.push(`the student (${channels.join(' and ')})`);
  const emailNote =
    n.studentEmail === 'NOT_CONFIGURED'
      ? 'email not configured'
      : n.studentEmail === 'FAILED'
        ? 'email failed'
        : n.studentEmail === 'NO_EMAIL'
          ? 'no student email on file'
          : null;
  if (parts.length === 0 && !emailNote) return 'No one was notified';
  return `Notified ${parts.length > 0 ? parts.join(' and ') : 'no one'}${emailNote ? ` — ${emailNote}` : ''}`;
};

const formatDate = (iso: string) => new Date(iso).toLocaleString();
const formatAmount = (entry: { amount: string; currency: { code: string; symbol: string | null } }) =>
  `${entry.currency.symbol ?? entry.currency.code} ${Number(entry.amount).toFixed(2)}`;

const performerName = (performedBy: TimelineAuditEvent['performedBy']) =>
  performedBy ? `${performedBy.firstName} ${performedBy.lastName}${performedBy.role ? ` (${performedBy.role.name})` : ''}` : 'Unknown user';

/** Batch/admission progress correction (Phase 64) — friendly labels for
 * the milestone fields `updateApplication`'s audit diff can carry; any
 * field not listed here still renders (using its raw key) rather than
 * being silently dropped. */
const MILESTONE_FIELD_LABELS: Record<string, string> = {
  offerLetterDate: 'Offer Letter date',
  coeNumber: 'Invitation (COE) number',
  coeDate: 'Invitation (COE) date',
  visaStatus: 'Visa status',
  departureDate: 'Departure date',
  arrivalDate: 'Arrival date',
  // Travel & Departure section (Phase 65)
  visaDecisionDate: 'Visa approval date',
  visaExpiryDate: 'Visa expiry date',
  visaRemarks: 'Visa remarks',
  airline: 'Airline',
  flightNumber: 'Flight number',
  departureAirport: 'Departure airport',
  arrivalAirport: 'Arrival airport',
  bookingReference: 'Booking reference',
  flightStatus: 'Flight status',
  departureStatus: 'Departure status',
  departureRemarks: 'Departure remarks',
  // Student Portal (Phase 67)
  phone: 'Phone number',
  email: 'Email address',
  address: 'Address',
  city: 'City',
  postalCode: 'Postal code',
  telephone: 'Telephone',
  whatsappNumber: 'WhatsApp number',
  emergencyContactName: 'Emergency contact name',
  emergencyContactPhone: 'Emergency contact phone',
  emergencyContactRelationship: 'Emergency contact relationship',
  // Phase 101
  passportNumber: 'Passport number',
  passportExpiryDate: 'Passport expiry date',
};

const formatMilestoneValue = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (field.toLowerCase().includes('date')) {
    const d = new Date(String(value));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  return String(value);
};

interface FeeChangeItemPayload {
  category: string;
  previous: number;
  new: number;
}

/** Fee change requested/approved/rejected all carry the same item-level
 * shape (category/previous/new), recorded once at request time and
 * mirrored onto the decision events so the Timeline never has to look
 * the original request back up (see studentFees.service.ts). */
const FeeChangeItemLines = ({ items }: { items: FeeChangeItemPayload[] }) => (
  <>
    {items.map((item, i) => (
      <Typography key={i} variant="body2">
        {item.category}: {item.previous.toFixed(2)} → {item.new.toFixed(2)}
      </Typography>
    ))}
  </>
);

const AuditTimelineItem = ({ event }: { event: TimelineAuditEvent }) => {
  const oldValue = (event.oldValue ?? {}) as Record<string, unknown>;
  const newValue = (event.newValue ?? {}) as Record<string, unknown>;
  const performer = performerName(event.performedBy);

  switch (event.action) {
    case 'FEE_CHANGE_REQUESTED': {
      const items = (newValue.items as FeeChangeItemPayload[] | undefined) ?? [];
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">
              Fee change requested ({String(newValue.requestNumber ?? '')}) by {performer}
            </Typography>
            <Chip size="small" color="warning" label="Pending Approval" />
          </Stack>
          <FeeChangeItemLines items={items} />
          {typeof newValue.reason === 'string' && newValue.reason && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.reason}
            </Typography>
          )}
        </>
      );
    }
    case 'FEE_CHANGE_APPROVED': {
      const items = (newValue.items as FeeChangeItemPayload[] | undefined) ?? [];
      const requestedBy = newValue.requestedBy as { firstName: string; lastName: string } | undefined;
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Fee change approved by {performer}</Typography>
            <Chip size="small" color="success" label="Approved — now active" />
          </Stack>
          {requestedBy && (
            <Typography variant="body2" color="text.secondary">
              Requested by: {requestedBy.firstName} {requestedBy.lastName}
            </Typography>
          )}
          <FeeChangeItemLines items={items} />
          {typeof newValue.decisionReason === 'string' && newValue.decisionReason && (
            <Typography variant="body2" color="text.secondary">
              Remark: {newValue.decisionReason}
            </Typography>
          )}
        </>
      );
    }
    case 'FEE_CHANGE_REJECTED': {
      const items = (newValue.items as FeeChangeItemPayload[] | undefined) ?? [];
      const requestedBy = newValue.requestedBy as { firstName: string; lastName: string } | undefined;
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Fee change rejected by {performer}</Typography>
            <Chip size="small" color="error" label="Rejected" />
          </Stack>
          {requestedBy && (
            <Typography variant="body2" color="text.secondary">
              Requested by: {requestedBy.firstName} {requestedBy.lastName}
            </Typography>
          )}
          <FeeChangeItemLines items={items} />
          {typeof newValue.decisionReason === 'string' && newValue.decisionReason && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.decisionReason}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Old fee remains active.
          </Typography>
        </>
      );
    }
    case 'FEE_SYNCED_FROM_DEFAULT':
      return (
        <Typography variant="body2">
          Fees synced from Master Data by {performer}
          {typeof oldValue.versionNumber === 'number' ? ` (v${oldValue.versionNumber} → v${String(newValue.versionNumber)})` : ` (now v${String(newValue.versionNumber)})`}
        </Typography>
      );
    case 'CHANGE_REQUESTED': {
      const field = String(newValue.fieldName ?? '');
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">
              {performer} requested to change {MILESTONE_FIELD_LABELS[field] ?? field}
              {typeof newValue.requestNumber === 'string' ? ` (${newValue.requestNumber})` : ''}
            </Typography>
            <Chip size="small" color="warning" label="Pending Approval" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {formatMilestoneValue(field, oldValue.value)} → {formatMilestoneValue(field, newValue.value)}
          </Typography>
        </>
      );
    }
    case 'CHANGE_APPROVED': {
      const field = String(newValue.fieldName ?? '');
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">
              {MILESTONE_FIELD_LABELS[field] ?? field} change approved by {performer}
            </Typography>
            <Chip size="small" color="success" label="Approved — now active" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {formatMilestoneValue(field, oldValue.value)} → {formatMilestoneValue(field, newValue.value)}
          </Typography>
        </>
      );
    }
    case 'CHANGE_REJECTED': {
      const field = String(newValue.fieldName ?? '');
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">
              {MILESTONE_FIELD_LABELS[field] ?? field} change rejected by {performer}
            </Typography>
            <Chip size="small" color="error" label="Rejected" />
          </Stack>
          {typeof newValue.decisionReason === 'string' && newValue.decisionReason && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.decisionReason}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Original value kept unchanged.
          </Typography>
        </>
      );
    }
    case 'DOCUMENT_APPROVED':
    case 'DOCUMENT_REJECTED':
      return (
        <>
          <Typography variant="body2">
            {String(newValue.documentType ?? 'Document')}
            {typeof newValue.version === 'number' && newValue.version > 1 ? ` (version ${newValue.version})` : ''}{' '}
            {event.action === 'DOCUMENT_APPROVED' ? 'approved' : 'rejected'} by {performer}
          </Typography>
          {typeof newValue.finalDocLabel === 'string' && (
            <Typography variant="body2" color="text.secondary">
              Final Documentation Checklist: {newValue.finalDocLabel}
            </Typography>
          )}
          {typeof newValue.rejectionReason === 'string' && newValue.rejectionReason && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.rejectionReason}
            </Typography>
          )}
          {notifiedSummary(newValue.notified) && (
            <Typography variant="caption" color="text.secondary">
              {notifiedSummary(newValue.notified)}
            </Typography>
          )}
        </>
      );
    case 'DOCUMENT_GENERATED':
    case 'DOCUMENT_REGENERATED':
      return (
        <Typography variant="body2">
          {String(newValue.templateKey ?? 'Document')} generated (v{String(newValue.version ?? '?')}) by {performer}
        </Typography>
      );
    case 'DOCUMENT_SENT':
      return (
        <Typography variant="body2">
          Document sent by {performer} via {String(newValue.method ?? '')} to {String(newValue.recipient ?? '')}
        </Typography>
      );
    case 'DOCUMENT_DOWNLOADED':
      return (
        <Typography variant="body2">
          {String(newValue.documentType ?? newValue.originalName ?? 'Document')} downloaded by {performer}
        </Typography>
      );
    case 'CONVERTED':
      return <Typography variant="body2">Converted from lead to student by {performer}</Typography>;
    case 'CONVERSION_BLOCKED':
      return (
        <Typography variant="body2">
          Conversion blocked ({String(newValue.reason ?? '')})
        </Typography>
      );
    case 'ASSIGNED':
      return (
        <Typography variant="body2">
          Assigned to Batch {String(newValue.batchNumber ?? '')} by {performer}
        </Typography>
      );
    case 'TRANSFERRED':
      return (
        <Typography variant="body2">
          Moved from Batch {String(oldValue.batchNumber ?? '')} to Batch {String(newValue.batchNumber ?? '')} by{' '}
          {performer}
        </Typography>
      );
    case 'DELETED':
      return (
        <Typography variant="body2">
          Removed from Batch {String(oldValue.batchNumber ?? '')} by {performer}
        </Typography>
      );
    case 'STATUS_CHANGED':
      return (
        <>
          <Typography variant="body2">
            Status changed: {String(oldValue.status ?? '').replace(/_/g, ' ')} →{' '}
            {String(newValue.status ?? '').replace(/_/g, ' ')} by {performer}
          </Typography>
          {typeof newValue.remark === 'string' && newValue.remark && (
            <Typography variant="body2" color="text.secondary">
              {newValue.remark}
            </Typography>
          )}
        </>
      );
    case 'EDITED': {
      // A batch's own departure date change carries a distinct
      // `batchNumber` key never present in the per-application milestone
      // diff below.
      if ('batchNumber' in newValue) {
        return (
          <Typography variant="body2">
            Batch {String(newValue.batchNumber ?? '')} departure date changed:{' '}
            {formatMilestoneValue('departureDate', oldValue.departureDate)} →{' '}
            {formatMilestoneValue('departureDate', newValue.departureDate)} by {performer}
          </Typography>
        );
      }
      // Final Documentation Checklist (Phase 95) — one tick or untick.
      if ('finalDocItem' in newValue) {
        const box =
          newValue.finalDocField === 'student' ? 'Student' : newValue.finalDocField === 'imc' ? 'IMC' : 'Not applicable';
        const verb =
          newValue.finalDocField === 'notApplicable'
            ? newValue.checked
              ? 'marked not applicable'
              : 'marked applicable again'
            : newValue.checked
              ? `ticked (${box})`
              : `unticked (${box})`;
        return (
          <>
            <Typography variant="body2">
              Final Documentation Checklist {verb} by {performer}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {String(newValue.finalDocLabel ?? newValue.finalDocItem)}
            </Typography>
          </>
        );
      }
      // FIS Exam registration (Phase 93) — exams.service.ts's own
      // `fisExam*` keys, never present in an application milestone diff.
      if ('fisExamStatus' in newValue) {
        const status = String(newValue.fisExamStatus ?? '').replace(/_/g, ' ').toLowerCase();
        return (
          <>
            <Typography variant="body2">
              FIS exam registration {'fisExamStatus' in oldValue ? 'updated' : 'recorded'} by {performer}: {status}
            </Typography>
            {'fisExamStatus' in oldValue && oldValue.fisExamStatus !== newValue.fisExamStatus && (
              <Typography variant="body2" color="text.secondary">
                Was: {String(oldValue.fisExamStatus ?? '').replace(/_/g, ' ').toLowerCase()}
              </Typography>
            )}
            {newValue.fisExamDate != null && (
              <Typography variant="body2" color="text.secondary">
                Exam date: {new Date(String(newValue.fisExamDate)).toLocaleDateString()}
              </Typography>
            )}
          </>
        );
      }
      const fields = Object.keys(newValue);
      return (
        <>
          <Typography variant="body2">Application details updated by {performer}</Typography>
          {fields.map((field) => (
            <Typography key={field} variant="body2" color="text.secondary">
              {MILESTONE_FIELD_LABELS[field] ?? field}: {formatMilestoneValue(field, oldValue[field])} →{' '}
              {formatMilestoneValue(field, newValue[field])}
            </Typography>
          ))}
        </>
      );
    }
    // Intake/batch transfers (Phase 94) — old/new carry the intake and
    // batch on each side, so the original assignment stays visible here.
    case 'INTAKE_TRANSFER_REQUESTED':
    case 'INTAKE_TRANSFER_APPROVED':
    case 'INTAKE_TRANSFER_REJECTED': {
      const verb =
        event.action === 'INTAKE_TRANSFER_REQUESTED'
          ? 'requested'
          : event.action === 'INTAKE_TRANSFER_APPROVED'
            ? 'approved'
            : 'rejected';
      const side = (v: Record<string, unknown>) =>
        `${String(v.intake ?? 'no intake')}${v.batchNumber ? `, batch ${String(v.batchNumber)}` : ', no batch'}`;
      return (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">
              Intake transfer {verb} by {performer}
            </Typography>
            {event.action === 'INTAKE_TRANSFER_REQUESTED' && (
              <Chip size="small" color="warning" label="Pending Approval" />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {side(oldValue)} → {side(newValue)}
          </Typography>
          {typeof newValue.reason === 'string' && newValue.reason && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.reason}
            </Typography>
          )}
          {typeof newValue.rejectionReason === 'string' && newValue.rejectionReason && (
            <Typography variant="body2" color="text.secondary">
              Rejection reason: {newValue.rejectionReason}
            </Typography>
          )}
          {typeof newValue.note === 'string' && newValue.note && (
            <Typography variant="body2" color="text.secondary">
              Note: {newValue.note}
            </Typography>
          )}
        </>
      );
    }
    case 'SIGNATURE_CREATED':
      return <Typography variant="body2">Signature saved by {performer}</Typography>;
    case 'SIGNATURE_REPLACED':
      return <Typography variant="body2">Signature replaced by {performer}</Typography>;
    case 'SIGNATURE_APPLIED':
      return (
        <Typography variant="body2">
          Signature applied to generated document (#{String(newValue.documentId ?? '')})
        </Typography>
      );
    case 'STUDENT_DROPOUT_RECORDED':
      return (
        <>
          <Typography variant="body2">
            Recorded as dropped out{newValue.droppedOutAt ? ` on ${new Date(String(newValue.droppedOutAt)).toLocaleDateString('en-GB')}` : ''} by {performer}
          </Typography>
          {typeof newValue.dropoutReason === 'string' && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.dropoutReason}
            </Typography>
          )}
        </>
      );
    case 'STUDENT_DROPOUT_REVERSED':
      return (
        <>
          <Typography variant="body2">Dropout record reversed by {performer}</Typography>
          {typeof newValue.reversalReason === 'string' && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.reversalReason}
            </Typography>
          )}
        </>
      );
    // Student Portal login management (Phase 100). No password, token or
    // link is ever recorded; only who did what and how delivery went.
    case 'PORTAL_ACCOUNT_CREATED':
      return (
        <Typography variant="body2">
          Student Portal login created ({String(newValue.loginEmail ?? '')}) by {performer}
        </Typography>
      );
    case 'PORTAL_INVITE_SENT':
      return (
        <>
          <Typography variant="body2">
            Portal invitation ({String(newValue.channel ?? '').toLowerCase()}) sent by {performer}
            {newValue.status === 'FAILED' ? ' — not delivered' : ''}
          </Typography>
          {typeof newValue.error === 'string' && (
            <Typography variant="body2" color="text.secondary">
              {newValue.error}
            </Typography>
          )}
        </>
      );
    case 'PORTAL_PASSWORD_RESET_SENT':
      return (
        <>
          <Typography variant="body2">
            Password reset email to {String(newValue.loginEmail ?? '')} requested by {performer}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {newValue.delivery === 'SENT'
              ? 'Sent'
              : newValue.delivery === 'NOT_CONFIGURED'
                ? 'Not sent — email is not configured'
                : `Not sent${typeof newValue.error === 'string' ? ` — ${newValue.error}` : ''}`}
          </Typography>
        </>
      );
    case 'PORTAL_LOGIN_EMAIL_CHANGED': {
      const notified = (newValue.notified ?? {}) as { previousAddress?: string; newAddress?: string };
      const delivery = (v?: string) => (v === 'SENT' ? 'told' : v === 'NOT_CONFIGURED' ? 'not told (email not configured)' : 'not told (email failed)');
      return (
        <>
          <Typography variant="body2">
            Portal login email changed by {performer}: {String(oldValue.loginEmail ?? '')} →{' '}
            {String(newValue.loginEmail ?? '')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Signed out of every session. Old address {delivery(notified.previousAddress)}; new address{' '}
            {delivery(notified.newAddress)}.
          </Typography>
        </>
      );
    }
    case 'PORTAL_ACCOUNT_DISABLED':
    case 'PORTAL_ACCOUNT_ENABLED':
      return (
        <>
          <Typography variant="body2">
            Student Portal login {event.action === 'PORTAL_ACCOUNT_DISABLED' ? 'disabled' : 'enabled'} by {performer}
          </Typography>
          {typeof newValue.reason === 'string' && (
            <Typography variant="body2" color="text.secondary">
              Reason: {newValue.reason}
            </Typography>
          )}
        </>
      );
    default:
      return <Typography variant="body2">{event.action}</Typography>;
  }
};

const AUDIT_ICON: Record<TimelineAuditEvent['action'], ReactElement> = {
  FEE_CHANGE_REQUESTED: <EditIcon color="warning" />,
  FEE_CHANGE_APPROVED: <CheckCircleIcon color="success" />,
  FEE_CHANGE_REJECTED: <CancelIcon color="error" />,
  FEE_SYNCED_FROM_DEFAULT: <SyncIcon color="action" />,
  DOCUMENT_APPROVED: <CheckCircleIcon color="success" />,
  DOCUMENT_REJECTED: <CancelIcon color="error" />,
  DOCUMENT_GENERATED: <DescriptionIcon color="primary" />,
  DOCUMENT_REGENERATED: <DescriptionIcon color="primary" />,
  DOCUMENT_SENT: <SendIcon color="primary" />,
  DOCUMENT_DOWNLOADED: <DownloadIcon color="action" />,
  CONVERTED: <CheckCircleIcon color="success" />,
  CONVERSION_BLOCKED: <BlockIcon color="error" />,
  ASSIGNED: <FlightTakeoffIcon color="primary" />,
  TRANSFERRED: <SwapHorizIcon color="action" />,
  DELETED: <CancelIcon color="error" />,
  STATUS_CHANGED: <TrendingFlatIcon color="secondary" />,
  EDITED: <EditIcon color="action" />,
  // Student Portal (Phase 67)
  CHANGE_REQUESTED: <EditIcon color="warning" />,
  CHANGE_APPROVED: <CheckCircleIcon color="success" />,
  CHANGE_REJECTED: <CancelIcon color="error" />,
  // Digital Signature (Phase 78)
  SIGNATURE_CREATED: <DrawOutlinedIcon color="primary" />,
  SIGNATURE_REPLACED: <DrawOutlinedIcon color="primary" />,
  SIGNATURE_APPLIED: <DescriptionIcon color="success" />,
  // Student Portal login management (Phase 100)
  PORTAL_ACCOUNT_CREATED: <KeyIcon color="primary" />,
  PORTAL_INVITE_SENT: <SendIcon color="primary" />,
  PORTAL_PASSWORD_RESET_SENT: <KeyIcon color="action" />,
  PORTAL_LOGIN_EMAIL_CHANGED: <EditIcon color="warning" />,
  PORTAL_ACCOUNT_DISABLED: <BlockIcon color="error" />,
  PORTAL_ACCOUNT_ENABLED: <CheckCircleIcon color="success" />,
  // Official dropout (Management Reports)
  STUDENT_DROPOUT_RECORDED: <BlockIcon color="error" />,
  STUDENT_DROPOUT_REVERSED: <CheckCircleIcon color="success" />,
  // Intake/batch transfers (Phase 94)
  INTAKE_TRANSFER_REQUESTED: <SwapHorizIcon color="warning" />,
  INTAKE_TRANSFER_APPROVED: <SwapHorizIcon color="success" />,
  INTAKE_TRANSFER_REJECTED: <SwapHorizIcon color="error" />,
};

const TimelineItem = ({ entry }: { entry: StudentTimelineEntry }) => {
  if (entry.kind === 'DOCUMENT_UPLOADED') {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <DescriptionIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            {documentDisplayName(entry.data.documentType.name, entry.data.customName)}
            {entry.data.version > 1 ? ` — replacement (version ${entry.data.version})` : ''} uploaded by{' '}
            {entry.data.uploadedBy.firstName} {entry.data.uploadedBy.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'QUALIFICATION_ADDED') {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <SchoolIcon color="secondary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            Qualification added: {entry.data.name} — {entry.data.institution}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'APPLICATION_CREATED') {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <AssignmentIcon color="action" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            Application {entry.data.applicationNumber} submitted to {entry.data.university.name} (
            {entry.data.course.name}) — status {entry.data.status}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'PAYMENT_RECORDED') {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        <PaymentsIcon color="action" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            Payment {entry.data.paymentNumber} recorded — {formatAmount(entry.data)} ({entry.data.category.name}) by{' '}
            {entry.data.recordedBy.firstName} {entry.data.recordedBy.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'PAYMENT_DECIDED') {
    const approved = entry.data.status === 'APPROVED';
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        {approved ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">
            Payment {entry.data.paymentNumber} {approved ? 'approved' : 'rejected'} by{' '}
            {entry.data.approvedBy?.firstName} {entry.data.approvedBy?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (entry.kind === 'AUDIT_EVENT') {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
        {AUDIT_ICON[entry.data.action]}
        <Box sx={{ flex: 1 }}>
          <AuditTimelineItem event={entry.data} />
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.at)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 2 }}>
      <ReceiptIcon color="success" />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">
          Receipt {entry.data.receipt?.receiptNumber} issued for payment {entry.data.paymentNumber}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(entry.at)}
        </Typography>
      </Box>
    </Paper>
  );
};

export const StudentTimelineTab = ({ studentId }: { studentId: number }) => {
  const timelineQuery = useQuery({
    queryKey: ['student-timeline', studentId],
    queryFn: () => getStudentTimeline(studentId),
  });

  if (timelineQuery.isLoading) return <CircularProgress size={24} sx={{ mt: 2 }} />;

  if (!timelineQuery.data?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No activity yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {timelineQuery.data.map((entry, i) => (
        <TimelineItem key={i} entry={entry} />
      ))}
    </Stack>
  );
};
